import moment from 'moment';
import type { useSearchStore } from '@/stores/searchStore';

type SearchStore = ReturnType<typeof useSearchStore>;

type RoomSearchParam = {
  adults: number;
  children: number[];
};

export function isWidgetMode(): boolean {
  return Boolean(window.onlWidgetRedirectURL?.trim());
}

function parseRoomSearchParam(value: unknown): RoomSearchParam | null {
  if (!value || typeof value !== 'object') return null;

  const room = value as { adults?: unknown; children?: unknown };
  const adults = Number(room.adults);
  if (!Number.isFinite(adults) || adults < 1) return null;

  const children = Array.isArray(room.children)
    ? room.children.map((age) => Number(age)).filter((age) => Number.isFinite(age))
    : [];

  return { adults, children };
}

export function parseRoomsFromUrl(roomsParam: string): RoomSearchParam[] | null {
  try {
    const parsed = JSON.parse(decodeURIComponent(roomsParam));
    if (!Array.isArray(parsed)) return null;

    const rooms = parsed
      .map(parseRoomSearchParam)
      .filter((room): room is RoomSearchParam => !!room);
    return rooms.length ? rooms : null;
  } catch {
    return null;
  }
}

export function buildBookingRedirectUrl(store: SearchStore): string {
  const baseUrl = window.onlWidgetRedirectURL?.trim();
  if (!baseUrl) {
    throw new Error('onlWidgetRedirectURL is not set');
  }

  const url = new URL(baseUrl);

  url.searchParams.set('dateIn', moment(store.selectedDateRange[0]).format('DD.MM.YYYY'));
  url.searchParams.set('dateOut', moment(store.selectedDateRange[1]).format('DD.MM.YYYY'));
  url.searchParams.set('autoSearch', 'true');

  const rooms = store.rooms.map((room) => ({
    adults: room.search.adults,
    children: room.search.children,
  }));
  url.searchParams.set('rooms', encodeURIComponent(JSON.stringify(rooms)));

  return url.toString();
}

export function redirectToBookingPage(store: SearchStore): void {
  const url = buildBookingRedirectUrl(store);
  const targetWindow = window.top ?? window;
  targetWindow.location.assign(url);
}
